import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { copySelectedReferences } from './copySelectedReferences'

export type TextReference = {
    field_id: string
    target?: { kind: string; uuid: string }
}

export type ReferenceChoice = TextReference & {
    label: string
    target_label?: string
    search_aliases: string[]
    display_value: string | null
    available: boolean
}

export type ReferenceToken = {
    start: number
    end: number
    raw: string
    reference: TextReference
}

const TOKEN_PATTERN = /::((?:[^:\n]|:(?!:))+)::/g
const FIELD_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?::[a-z0-9]+(?:-[a-z0-9]+)*)+$/
const TARGET_KIND_PATTERN = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

export function normalizeReferenceSearch(value: string) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('de')
        .replace(/ß/g, 'ss')
        .trim()
}

export function getReferenceChoiceLabel(choice: ReferenceChoice) {
    return [choice.label, choice.target_label].filter(Boolean).join(' · ')
}

function parseReferenceBody(body: string): TextReference | undefined {
    const [fieldId, targetText, ...extra] = body.split('@')
    if (extra.length || !FIELD_ID_PATTERN.test(fieldId)) {
        return undefined
    }

    const reference: TextReference = { field_id: fieldId }
    if (targetText !== undefined) {
        const separator = targetText.indexOf(':')
        const kind = targetText.slice(0, separator)
        const uuid = targetText.slice(separator + 1)
        if (separator < 0 || !TARGET_KIND_PATTERN.test(kind) || !UUID_PATTERN.test(uuid)) {
            return undefined
        }
        reference.target = { kind, uuid }
    }
    return reference
}

export function parseReferenceTokens(text: string): ReferenceToken[] {
    const pattern = new RegExp(TOKEN_PATTERN)
    const tokens: ReferenceToken[] = []
    for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
        const reference = parseReferenceBody(match[1])
        if (!reference) {
            pattern.lastIndex = match.index + 2
            continue
        }
        tokens.push({ start: match.index, end: match.index + match[0].length, raw: match[0], reference })
    }
    return tokens
}

export function serializeReferenceToken(reference: TextReference): string {
    const fieldId = reference.field_id
    if (!FIELD_ID_PATTERN.test(fieldId)) {
        throw new Error('Invalid reference field ID')
    }
    if (!reference.target) {
        return `::${fieldId}::`
    }

    const { kind, uuid } = reference.target
    if (!TARGET_KIND_PATTERN.test(kind) || !UUID_PATTERN.test(uuid)) {
        throw new Error('Invalid reference target')
    }
    return `::${fieldId}@${kind}:${uuid}::`
}

export function findReferenceChoice(reference: TextReference, choices: ReferenceChoice[]): ReferenceChoice | undefined {
    return choices.find(
        (choice) =>
            choice.field_id === reference.field_id &&
            choice.target?.kind === reference.target?.kind &&
            choice.target?.uuid === reference.target?.uuid
    )
}

const ReferenceChoicesContext = createContext<ReferenceChoice[] | undefined>(undefined)
const ReferenceEditHrefContext = createContext<((reference: TextReference) => string | undefined) | undefined>(undefined)

export function ReferenceChoicesProvider({
    choices,
    getEditHref,
    children,
}: {
    choices: ReferenceChoice[]
    getEditHref?: (reference: TextReference) => string | undefined
    children: ReactNode
}) {
    useEffect(() => {
        document.addEventListener('copy', copySelectedReferences, true)
        return () => document.removeEventListener('copy', copySelectedReferences, true)
    }, [])

    return (
        <ReferenceEditHrefContext.Provider value={getEditHref}>
            <ReferenceChoicesContext.Provider value={choices}>{children}</ReferenceChoicesContext.Provider>
        </ReferenceEditHrefContext.Provider>
    )
}

export function useReferenceChoices() {
    return useContext(ReferenceChoicesContext)
}

export function useReferenceEditHref() {
    return useContext(ReferenceEditHrefContext)
}
